<template>
  <!-- Scoreboard layout (same as LeagueEventTile) -->
  <SectionRow
    v-if="showTeamLogos"
    row-class="league-score"
    :can-edit="showEdit && canEditEvent"
    :show-menu="showPinnedActions ? canEditEvent : showMenu"
    :hide-actions="hideActions"
    :show-lock="!hideActions && !isTeamMember"
    @click="emit('navigate'); router.push(`/event/${event._id}`)"
    @menu-open="emit('menuOpen')"
    @menu-close="emit('menuClose')"
  >
    <!-- Time column (desktop) -->
    <div class="league-score-time-col">
      <span class="league-score-time-col__time">{{ event.timeFrom || '' }}</span>
      <span v-if="!hasGameResult" class="league-score-time-col__date">{{ dateText }}</span>
      <span v-else class="league-score-time-col__status">{{ $t('eventTile.result') }}</span>
    </div>

    <!-- Home team -->
    <div class="league-score-team">
      <div class="league-score-logo-wrap">
        <img
          v-if="event?.meta?.homeTeam?.imageUrl"
          :src="getTeamImage(event.meta.homeTeam.imageUrl)"
          class="league-score-logo"
        />
        <div v-else class="league-score-logo league-score-logo--placeholder">
          <v-icon size="20" color="rgba(var(--v-theme-sheetText), 0.2)">mdi-shield-outline</v-icon>
        </div>
        <span class="league-score-badge" :class="whiteTeamStatus.color === 'green' ? 'badge-ok' : 'badge-warn'">{{ whiteTeamStatus.resultString }}</span>
      </div>
      <span class="league-score-team-name">{{ event.meta?.homeTeam?.shortName || removeTrailingNumbers(event.meta?.homeTeam?.name ?? '') || $t('eventTile.homeTeam') }}</span>
    </div>

    <!-- Home goals (if result exists) -->
    <span v-if="hasGameResult" class="league-score-goals" :class="{ 'league-score-goals--winner': homeGoals > awayGoals }">{{ homeGoals }}</span>

    <!-- Center: status + date + time + place -->
    <div class="league-score-center">
      <span v-if="showLeagueHeader" class="league-score-league">{{ $t('friendly-match') }}</span>
      <span class="league-score-vs">VS</span>
      <template v-if="hasGameResult && !showLeagueHeader">
        <span class="league-score-status league-score-status--mobile">{{ $t('eventTile.played') }}</span>
      </template>
      <template v-else>
        <span class="league-score-date league-score-date--mobile" :class="{ 'league-score-date--small': showLeagueHeader }">{{ dateText }}</span>
      </template>
      <span class="league-score-time league-score-time--mobile">{{ event.timeFrom || '' }}</span>
      <span v-if="placeText" class="league-score-place">{{ placeText }}</span>
    </div>

    <!-- Away goals (if result exists) -->
    <span v-if="hasGameResult" class="league-score-goals" :class="{ 'league-score-goals--winner': awayGoals > homeGoals }">{{ awayGoals }}</span>

    <!-- Away team -->
    <div class="league-score-team">
      <div class="league-score-logo-wrap">
        <img
          v-if="event?.meta?.awayTeam?.imageUrl"
          :src="getTeamImage(event.meta.awayTeam.imageUrl)"
          class="league-score-logo"
        />
        <div v-else class="league-score-logo league-score-logo--placeholder">
          <v-icon size="20" color="rgba(var(--v-theme-sheetText), 0.2)">mdi-shield-outline</v-icon>
        </div>
        <span class="league-score-badge" :class="blueTeamStatus.color === 'green' ? 'badge-ok' : 'badge-warn'">{{ blueTeamStatus.resultString }}</span>
      </div>
      <span class="league-score-team-name">{{ event.meta?.awayTeam?.shortName || removeTrailingNumbers(event.meta?.awayTeam?.name ?? '') || $t('eventTile.awayTeam') }}</span>
    </div>

    <span v-if="isCanceled" class="league-score-canceled-mask"></span>
    <span v-if="isCanceled" class="league-score-canceled-stamp">{{ canceledStampLabel }}</span>

    <!-- Context menu items -->
    <template #menu="{ close }">
      <div class="context-menu-item" @click="router.push(`/event/${event._id}`); close()">
        <span>{{ $t('eventTile.event') }}</span>
        <v-icon size="18">mdi-chevron-right</v-icon>
      </div>
      <div class="context-menu-divider"></div>
      <div class="context-menu-item" @click="router.push(`/eventdatalog/${event._id}`); close()">
        <span>{{ $t('eventTile.gameLog') }}</span>
        <v-icon size="18">mdi-chevron-right</v-icon>
      </div>
      <template v-if="canEditEvent && showPinnedActions">
        <div class="context-menu-divider"></div>
        <div class="context-menu-item" @click="emit('unpinEvent', event); close()">
          <span>Odepnout</span>
          <v-icon size="18">mdi-pin-off-outline</v-icon>
        </div>
        <div class="context-menu-divider"></div>
        <div class="context-menu-item" @click="emit('editEvent', event); close()">
          <span>{{ $t('eventTile.edit') }}</span>
          <v-icon size="18">mdi-pencil-outline</v-icon>
        </div>
        <div class="context-menu-divider"></div>
        <div class="context-menu-item context-menu-item--danger" @click="emit('cancelEvent', event); close()">
          <span>Zrušit</span>
          <v-icon size="18">mdi-close-circle-outline</v-icon>
        </div>
      </template>
      <template v-else-if="canEditEvent">
        <div class="context-menu-divider"></div>
        <div class="context-menu-item" @click="emit('editEvent', event); close()">
          <span>{{ $t('eventTile.edit') }}</span>
          <v-icon size="18">mdi-pencil-outline</v-icon>
        </div>
        <div class="context-menu-divider"></div>
        <div class="context-menu-item context-menu-item--danger" @click="emit('deleteEvent', event); close()">
          <span>{{ $t('eventTile.deleteEvent') }}</span>
          <v-icon size="18">mdi-trash-can-outline</v-icon>
        </div>
      </template>
    </template>
  </SectionRow>

  <!-- Default layout (without team logos) -->
  <EventTileBase
    v-else
    :event="event"
    :showEdit="showEdit"
    :canEdit="canEditEvent"
    :showMenu="showMenu"
    :showPinnedActions="showPinnedActions"
    :showLeagueLogo="showLeagueLogo"
    :hideActions="hideActions"
    :showLock="!hideActions && !isTeamMember"
    @navigate="handleNavigate"
    @unpinEvent="emit('unpinEvent', $event)"
    @deleteEvent="emit('deleteEvent', $event)"
    @cancelEvent="emit('cancelEvent', $event)"
    @editEvent="emit('editEvent', $event)"
    @menuOpen="emit('menuOpen')"
    @menuClose="emit('menuClose')"
  >
    <template #detail>
      <div class="match-tile-teams">
        <span class="match-tile-team">
          <img v-if="event?.meta?.homeTeam?.imageUrl" :src="getTeamImage(event.meta.homeTeam.imageUrl)" class="match-tile-team-logo" />
          <span :style="{ color: whiteTeamStatus.color === 'green' ? '#4caf50' : '#ef5350' }">{{ whiteTeamStatus.resultString }}</span>
        </span>
        <span class="match-tile-vs">vs</span>
        <span class="match-tile-team">
          <img v-if="event?.meta?.awayTeam?.imageUrl" :src="getTeamImage(event.meta.awayTeam.imageUrl)" class="match-tile-team-logo" />
          <span :style="{ color: blueTeamStatus.color === 'green' ? '#4caf50' : '#ef5350' }">{{ blueTeamStatus.resultString }}</span>
        </span>
      </div>
    </template>
  </EventTileBase>
</template>

<script setup lang="ts">
import { computed, toRef, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'
import type { IEvent } from '@/types'
import EventTileBase from '@/components/EventTiles/EventTileBase.vue'
import SectionRow from '@/components/SectionRow.vue'
import { useEventPermissions } from '@/composables/useEventPermissions'
import { useMatchTeamStatus } from '@/composables/useMatchTeamStatus'
import { getTeamImage } from '@/utils/imageUtils'
import { removeTrailingNumbers } from '@/utils/teamUtils'
import { useUserStore } from '@/stores/user/userStore'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  event: IEvent
  fullDate?: boolean
  showEdit: boolean
  showMenu?: boolean
  showPinnedActions?: boolean
  showLeagueLogo?: boolean
  showTeamLogos?: boolean
  showLeagueHeader?: boolean
  hideActions?: boolean
}>()

const router = useRouter()
const userStore = useUserStore()
useI18n({ useScope: 'global' })
const emit = defineEmits(['unpinEvent', 'deleteEvent', 'cancelEvent', 'editEvent', 'navigate', 'menuOpen', 'menuClose'])

const { canEditEvent } = useEventPermissions(toRef(props, 'event'), 'simple')
const { whiteTeamStatus, blueTeamStatus } = useMatchTeamStatus(toRef(props, 'event'))

/* ── team membership check ── */
const getIdFromRef = (ref: any): string | null => {
  if (!ref) return null
  if (typeof ref === 'string') return ref
  return ref._id || ref.id || null
}

const isTeamMember = computed(() => {
  const userTeams = userStore.user?._teams ?? []
  if (!userTeams.length) return false
  const ids = [
    getIdFromRef(props.event?._team),
    getIdFromRef(props.event?.meta?.homeTeam),
    getIdFromRef(props.event?.meta?.awayTeam),
  ].filter(Boolean).map(String)
  return ids.some(id => userTeams.includes(id))
})

const handleNavigate = () => {
  if (isTeamMember.value) {
    router.push(`/event/${props.event._id}`)
  }
}

// Date text for compact layout
const instance = getCurrentInstance()
const $formatDate = instance?.appContext.config.globalProperties.$formatDate

const dateText = computed(() => {
  if (!props.event.dateFrom) return ''
  const from = (props.event as any).dateFromRaw ?? props.event.dateFrom
  const to = (props.event as any).dateToRaw ?? props.event.dateTo
  return $formatDate ? $formatDate(from, to) : String(from)
})

const placeText = computed(() => {
  return props.event.place || props.event.location || props.event.meta?.place || ''
})

// Check if game has final result
const hasGameResult = computed(() => {
  const gs = props.event.gameStatus as any
  return gs?.home?.goals !== undefined && gs?.away?.goals !== undefined
})

const homeGoals = computed(() => (props.event.gameStatus as any)?.home?.goals ?? 0)
const awayGoals = computed(() => (props.event.gameStatus as any)?.away?.goals ?? 0)

const isCanceled = computed(() => Boolean((props.event as any)?.canceled))
const canceledStampLabel = computed(() => {
  const key = 'eventTile.canceledStamp'
  const label = t(key)
  return label === key ? 'CANCELED' : label
})
</script>

<style lang="scss" scoped>
/* ── Scoreboard layout (deep for SectionRow child) ── */
:deep(.league-score) {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  gap: 10px;
  cursor: pointer;
  position: relative;
}

:deep(.league-score)::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 16px;
  right: 16px;
  height: 1px;
  background: linear-gradient(90deg,
      transparent 0%,
      rgba(var(--v-theme-sheetText), 0.12) 15%,
      rgba(var(--v-theme-sheetText), 0.12) 85%,
      transparent 100%);
}

:deep(.league-score):active {
  background: rgba(var(--v-theme-sheetText), 0.04);
}

:deep(.league-score-canceled-mask) {
  position: absolute;
  inset: 0;
  z-index: 2;
  background:
    linear-gradient(180deg, rgba(7, 10, 16, 0.16) 0%, rgba(7, 10, 16, 0.3) 100%),
    rgba(12, 16, 24, 0.18);
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.04),
    inset 0 0 24px rgba(0, 0, 0, 0.14);
  backdrop-filter: saturate(0.72) blur(1.2px);
  pointer-events: none;
}

:deep(.league-score-canceled-stamp) {
  position: absolute;
  right: 36px;
  top: 10px;
  z-index: 3;
  font-size: 0.9rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(245, 124, 0, 0.98);
  background: rgba(0, 0, 0, 0.06);
  border: 3px solid rgba(245, 124, 0, 0.95);
  border-radius: 6px;
  box-shadow:
    0 6px 14px rgba(0, 0, 0, 0.35),
    0 1px 0 rgba(255, 255, 255, 0.2) inset,
    0 0 0 1px rgba(245, 124, 0, 0.28) inset;
  text-shadow:
    0 0 1px rgba(245, 124, 0, 0.55),
    0 0 0.5px rgba(245, 124, 0, 0.75);
  padding: 5px 12px 4px;
  transform: rotate(-14deg);
  transform-origin: center;
  opacity: 0.9;
  filter: saturate(0.92) contrast(1.05);
  pointer-events: none;
  backdrop-filter: blur(0.4px);
}

/* ── Time column (desktop only) ── */
:deep(.league-score-time-col) {
  display: none;
}

/* ── VS text ── */
:deep(.league-score-vs) {
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 0.85rem;
  font-weight: 700;
  color: rgba(var(--v-theme-sheetText), 0.25);
  letter-spacing: 0.08em;
  line-height: 1;
}

@media (min-width: 600px) {
  :deep(.league-score-time-col) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 52px;
    flex-shrink: 0;
    gap: 2px;
    padding-right: 8px;
    border-right: 1px solid rgba(var(--v-theme-sheetText), 0.08);
    margin-right: 4px;
  }

  :deep(.league-score-time-col__time) {
    font-size: 0.9rem;
    font-weight: 700;
    color: rgba(var(--v-theme-sheetText), 0.85);
    line-height: 1.15;
  }

  :deep(.league-score-time-col__date) {
    font-size: 0.65rem;
    font-weight: 500;
    color: rgba(var(--v-theme-sheetText), 0.4);
    white-space: nowrap;
  }

  :deep(.league-score-time-col__status) {
    font-size: 0.62rem;
    font-weight: 600;
    color: rgba(var(--v-theme-sheetText), 0.4);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  :deep(.league-score-date--mobile) {
    display: none;
  }

  :deep(.league-score-time--mobile) {
    display: none;
  }

  :deep(.league-score-status--mobile) {
    display: none;
  }

  :deep(.league-score-center) {
    flex: 0 0 auto;
    min-width: 24px;
  }

  :deep(.league-score-place) {
    display: block;
  }
}

:deep(.league-score-team) {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 72px;
  flex-shrink: 0;
}

:deep(.league-score-logo-wrap) {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.league-score-logo) {
  width: 44px;
  height: 44px;
  object-fit: contain;
  border-radius: 8px;
  flex-shrink: 0;
}

:deep(.league-score-logo--placeholder) {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--v-theme-sheetText), 0.04);
}

:deep(.league-score-badge) {
  position: absolute;
  bottom: -6px;
  right: -10px;
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1;
  padding: 3px 5px;
  border-radius: 7px;
  white-space: nowrap;
  letter-spacing: 0.02em;
  border: 2px solid rgba(var(--v-theme-sheetSection), 1);
}

:deep(.badge-ok) {
  background: #4caf50;
  color: #fff;
}

:deep(.badge-warn) {
  background: #ef5350;
  color: #fff;
}

:deep(.league-score-team-name) {
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(var(--v-theme-sheetText), 0.6);
  text-align: center;
  line-height: 1.15;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

:deep(.league-score-center) {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 0;
  overflow: hidden;
}

:deep(.league-score-league) {
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(var(--v-theme-sheetText), 0.5);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

:deep(.league-score-date) {
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(var(--v-theme-sheetText), 0.7);
}

:deep(.league-score-date--small) {
  font-size: 0.7rem;
  font-weight: 400;
  color: rgba(var(--v-theme-sheetText), 0.45);
}

:deep(.league-score-goals) {
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: rgba(var(--v-theme-sheetText), 0.35);
  line-height: 1;
  min-width: 32px;
  text-align: center;
}

:deep(.league-score-goals--winner) {
  color: rgba(var(--v-theme-sheetText), 1);
}

:deep(.league-score-status) {
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(var(--v-theme-sheetText), 0.5);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

:deep(.league-score-time) {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(var(--v-theme-sheetText), 0.7);
}

:deep(.league-score-place) {
  font-size: 0.72rem;
  font-weight: 400;
  color: rgba(var(--v-theme-sheetText), 0.35);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  display: none;
}

:deep(.league-score-chevron) {
  color: rgba(var(--v-theme-sheetText), 0.18);
  flex-shrink: 0;
}

:deep(.league-score-menu) {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: rgba(var(--v-theme-sheetText), 0.4);
  border-radius: 8px;
  transition: background 0.15s ease;
}

:deep(.league-score-menu):active {
  background: rgba(var(--v-theme-sheetText), 0.08);
}

/* ── Default detail layout ── */
.match-tile-teams {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 3px;
}

.match-tile-team {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.72rem;
  font-weight: 600;
}

.match-tile-team-logo {
  width: 18px;
  height: 18px;
  object-fit: contain;
  border-radius: 4px;
}

.match-tile-vs {
  font-size: 0.68rem;
  font-weight: 400;
  color: rgba(var(--v-theme-sheetText), 0.35);
  text-transform: uppercase;
}
</style>
